import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  TooltipProps,
} from "recharts";
import {
  AXIS_STYLES,
  GRID_STYLES,
} from "src/components/Referral-partner/Analytics/styles/chart.styles";
import { AssetDataPoint } from "src/components/Referral-partner/Analytics/analyticsTypes";

interface AssetsTradedProps {
  data: AssetDataPoint[];
}

const generateColors = (count: number) => {
  const ASSET_COLORS = [
    "#e66d79", // red-pink
    "#4a90e2", // blue
    "#50e3c2", // teal
    "#f5a623", // orange
    "#9013fe", // purple
    "#7ed321", // green
    "#d0021b", // red
    "#4a4a4a", // dark grey
    "#9b9b9b", // light grey
    "#f8e71c", // yellow
    "#8b572a", // brown
    "#2ca9e1", // sky blue
    "#ff9f40", // peach
    "#c370d8", // lilac
    "#a6e22d", // chartreuse
    "#e74c3c", // crimson
    "#34495e", // navy blue
    "#bdc3c7", // silver
    "#f1c40f", // gold
    "#795548", // bronze
  ];

  if (count <= ASSET_COLORS.length) {
    return ASSET_COLORS.slice(0, count);
  }

  const colors = [...ASSET_COLORS];

  const generateHSLColor = (index: number, totalColors: number) => {
    const goldenRatioConjugate = 0.618033988749895;
    const hue = (index * goldenRatioConjugate) % 1;

    return `hsl(${Math.floor(hue * 360)}, 70%, 45%)`;
  };

  for (let i = ASSET_COLORS.length; i < count; i++) {
    colors.push(generateHSLColor(i, count));
  }

  return colors;
};

export const AssetsTraded = (props: AssetsTradedProps) => {
  const { data } = props;

  // Extract unique asset names across all months to ensure consistent colors
  const uniqueAssets = Array.from(
    new Set(data.flatMap((month) => month.topAssets.map((asset) => asset.name))),
  );

  const topAssets = useMemo(() => {
    // Calculate total value for each asset across all timeframes
    const assetTotals = uniqueAssets.map((assetName) => {
      const totalPercentage = data.reduce((sum, month) => {
        const asset = month.topAssets.find((a) => a.name === assetName);
        return sum + (asset ? asset.percentage : 0);
      }, 0);

      return {
        name: assetName,
        totalPercentage,
      };
    });

    return assetTotals
      .sort((a, b) => b.totalPercentage - a.totalPercentage)
      .map((asset) => asset.name);
  }, [data, uniqueAssets]);

  const assetColorMap = useMemo(() => {
    const colors = generateColors(topAssets.length);
    const map = new Map();
    topAssets.forEach((assetName, index) => {
      map.set(assetName, colors[index]);
    });
    return map;
  }, [topAssets]);

  // Custom tooltip with proper type definitions
  const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
      const monthData = data.find((item) => item.timestamp === label);

      if (!monthData) return null;

      // Filter assets to only show ones that have a value > 0
      const visibleAssets = monthData.topAssets.filter((asset) => {
        // Only show assets that are in our top assets list and have non-zero values
        return topAssets.includes(asset.name) && asset.percentage > 0;
      });

      const styles = {
        container: {
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          padding: "12px 16px",
          borderRadius: "6px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1), 0 0 1px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          fontSize: "12px",
          minWidth: "140px",
        },
        header: {
          color: "#374151",
          fontWeight: "600",
          marginBottom: "8px",
          borderBottom: "1px solid #E5E7EB",
          paddingBottom: "4px",
        },
        row: {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "4px",
        },
        label: {
          color: "#6B7280",
          marginRight: "16px",
          display: "flex",
          alignItems: "center",
        },
        dot: {
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          marginRight: "6px",
        },
        value: {
          color: "#111827",
          fontWeight: "500",
        },
        percentage: {
          paddingLeft: "4px",
          color: "#6B7280",
        },
      };

      return (
        <div className="custom-tooltip" style={styles.container}>
          <p className="label" style={{ fontWeight: "bold", marginBottom: "5px" }}>
            {label}
          </p>
          {visibleAssets.length > 0 ? (
            visibleAssets.map((asset) => (
              <div key={asset.name} style={styles.row}>
                <div style={styles.label}>
                  <div
                    style={{
                      ...styles.dot,
                      backgroundColor: assetColorMap.get(asset.name),
                    }}
                  />
                  {asset.name}:
                </div>
                <span style={styles.value}>{asset.percentage}%</span>
              </div>
            ))
          ) : (
            <div>No trading activity in this period</div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer id="deposit-volume-chart" height="80%" width="100%">
      <BarChart
        data={data}
        margin={{
          top: 20,
          right: 10,
        }}>
        <CartesianGrid vertical={false} {...GRID_STYLES} />
        <XAxis dataKey="timestamp" {...AXIS_STYLES} tick={{ fontSize: 9 }} />
        <YAxis
          {...AXIS_STYLES}
          tick={false} // keeping Y axis for styling but hiding values
        />
        <Tooltip content={<CustomTooltip />} />
        {topAssets.map((assetName) => (
          <Bar
            key={assetName}
            dataKey={(dataPoint) => {
              const asset = dataPoint.topAssets.find((a: any) => a.name === assetName);
              return asset && typeof asset.percentage === "number" && asset.percentage > 0
                ? asset.percentage
                : 0;
            }}
            name={assetName}
            stackId="a"
            fill={assetColorMap.get(assetName)}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
